import { useState } from 'react';
import { apiClient, APIError } from '@/api/client';
import { authClient } from '@/lib/auth-client';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TimesheetDateRangeSelector } from './components/TimesheetDateRangeSelector';
import { PayrollSummaryTable } from './components/PayrollSummaryTable';
import { JobHoursSection } from './components/JobHoursSection';
import { UnmatchedEventsSection } from './components/UnmatchedEventsSection';

interface PayrollWorker {
  name: string;
  dailyHours: Record<string, number>;
  totalHours: number;
}

interface JobHoursGroup {
  jobCode: string;
  jobAddress: string;
  events: Array<{
    date: string;
    timeRange: string;
    worker: string;
    eventName: string;
    hours: number;
  }>;
  totalHours: number;
}

interface UnmatchedEvent {
  date: string;
  timeRange: string;
  worker: string;
  eventName: string;
  hours: number;
  eventId?: string;
  calendarId?: string;
}

interface TimesheetData {
  dateRange: {
    start: string;
    end: string;
  };
  payroll: {
    workers: PayrollWorker[];
    dates: string[];
    dateTotals: Record<string, number>;
  };
  jobHours: JobHoursGroup[];
  unmatchedEvents: UnmatchedEvent[];
  totalEvents: number;
}

export default function Timesheets() {
  const { toast } = useToast();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [timesheetData, setTimesheetData] = useState<TimesheetData | null>(null);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const [googleTokenExpired, setGoogleTokenExpired] = useState(false);

  // Format date to YYYY-MM-DD without timezone conversion
  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleStartDateChange = (date: Date | undefined) => {
    setStartDate(date);
    if (date) {
      setEndDate(date);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
  };

  const generateTimesheet = async () => {
    if (!startDate || !endDate) {
      return;
    }

    setIsGenerating(true);
    try {
      const response = await apiClient.get<TimesheetData>('/reports/timesheets', {
        params: {
          startDate: formatDateString(startDate),
          endDate: formatDateString(endDate),
        },
      });
      setTimesheetData(response);
      return response;
    } catch (error) {
      console.error('Failed to generate timesheets:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Missing dates',
        description: 'Please select both start and end dates',
        variant: 'destructive',
      });
      return;
    }

    if (startDate > endDate) {
      toast({
        title: 'Invalid date range',
        description: 'Start date must be before end date',
        variant: 'destructive',
      });
      return;
    }

    try {
      setGoogleTokenExpired(false);
      const response = await generateTimesheet();
      if (response) {
        toast({
          title: 'Timesheets Generated',
          description: `Found ${response.totalEvents} events across ${response.payroll.workers.length} workers`,
        });
      }
    } catch (error) {
      if (error instanceof APIError && error.code === 'GOOGLE_TOKEN_EXPIRED') {
        setGoogleTokenExpired(true);
      } else {
        toast({
          title: 'Generation Failed',
          description: error instanceof Error ? error.message : 'Failed to generate timesheets',
          variant: 'destructive',
        });
      }
    }
  };

  const handleEventMatched = async () => {
    try {
      await generateTimesheet();
      toast({
        title: 'Timesheet Refreshed',
        description: 'Timesheet data has been updated with the matched event',
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: 'Failed to refresh timesheet data',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Timesheets</h1>
        <p className="mt-2 text-sm text-gray-700">
          Generate payroll and job hours reports from worker calendar events
        </p>
      </div>

      <TimesheetDateRangeSelector
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onGenerate={handleGenerate}
        isGenerating={isGenerating}
        startDatePickerOpen={startDatePickerOpen}
        endDatePickerOpen={endDatePickerOpen}
        setStartDatePickerOpen={setStartDatePickerOpen}
        setEndDatePickerOpen={setEndDatePickerOpen}
      />

      {googleTokenExpired && (
        <Alert variant="destructive" className="mt-6">
          <AlertTitle>Google session expired</AlertTitle>
          <AlertDescription className="mt-2">
            <p>Your Google Calendar connection has expired. Please sign in with Google again to reconnect.</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => authClient.signIn.social({ provider: 'google', callbackURL: window.location.href })}
            >
              Reconnect Google Account
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {timesheetData && (
        <div className="space-y-6">
          <PayrollSummaryTable
            workers={timesheetData.payroll.workers}
            dates={timesheetData.payroll.dates}
            dateTotals={timesheetData.payroll.dateTotals}
          />

          <JobHoursSection jobHours={timesheetData.jobHours} />

          <UnmatchedEventsSection
            unmatchedEvents={timesheetData.unmatchedEvents}
            onEventMatched={handleEventMatched}
          />
        </div>
      )}
    </div>
  );
}
